import { useCallback, useContext, useEffect, useState } from 'react';

import moment from 'moment/moment';
import { useDispatch, useSelector } from 'react-redux';

import { OptionType } from '@shared/ui/MultiSelect';

import { editProgress, getProgressMediaList } from '../../../../store/slices/dashboardSlice/action';
import {
    dashboardActions,
    progressSelector,
} from '../../../../store/slices/dashboardSlice/dashboardSlice';
import { ProgressContext } from '../../Context/ProgressContext';
import { useUploadProgressMediaItem } from '../../hooks/useUploadProgressMediaItem';

export const useModalConstructionProgress = () => {
    const {
        appendPendingUploadMediaItems,
        clearPendingUploadMediaItems,
        isUploadQueueStarted,
        getUploadMediaListSnapshot,
        setUploadQueueStarted,
        setUploadMediaMeta,
        clearUploadedProgressMediaList,
    } = useContext(ProgressContext);
    const dispatch = useDispatch();
    const { uploadMediaItem } = useUploadProgressMediaItem();

    // @ts-ignore
    const { modalMode, currentProgressId, albums, temporalAlbums } = useSelector(
        // @ts-ignore
        (state) => state.dashboardReducer
    );

    // @ts-ignore
    const progressEntities = useSelector(progressSelector.selectEntities);
    const currentProgress = progressEntities[currentProgressId];

    const [formState, setFormState] = useState({
        date: currentProgress ? currentProgress.date : moment().format(),
        description: currentProgress ? currentProgress.description : '',
        albums: [],
    });

    useEffect(() => {
        if (currentProgress)
            setFormState({
                date: currentProgress.date,
                description: currentProgress.description,
                albums: currentProgress.albums.map((album) => ({
                    value: album.id,
                    title: album.title,
                })),
            });
    }, [currentProgress]);

    const onDate = useCallback(
        (value: string) => {
            setFormState({ ...formState, date: value });
        },
        [formState]
    );
    const onDescription = useCallback(
        (name: string, value: string) => {
            setFormState({ ...formState, description: value });
        },
        [formState]
    );

    const onAddNewAlbum = (album: OptionType) => {
        dispatch(dashboardActions.addAlbums(album));
        setFormState({ ...formState, albums: [...formState.albums, album] });
    };

    const deleteSelectedAlbum = (value: string) => {
        const updatedAlbums = formState.albums.filter(
            (album) => String(album.value) !== String(value)
        );

        setFormState({ ...formState, albums: updatedAlbums });
    };

    const onChangeAlbum = (value: string) => {
        const isFoundAlbumSelected = formState.albums.some(
            (album) => String(album.value) === String(value)
        );

        const updatedAlbums = formState.albums.filter((album) => {
            if (isFoundAlbumSelected) {
                return String(album.value) !== String(value);
            }
            return true;
        });

        const newAlbum = [...albums, ...temporalAlbums].find(
            (album) => String(album.value) === String(value)
        );
        setFormState({ ...formState, albums: [...updatedAlbums, newAlbum] });
    };

    const isEdit = !!currentProgressId;
    const onCancel = useCallback(() => {
        if (!isEdit) {
            clearPendingUploadMediaItems();
        }
        dispatch(dashboardActions.changeCurrentProgressId(null));
        dispatch(dashboardActions.onModalMode(null));
    }, [clearPendingUploadMediaItems, dispatch, isEdit]);

    const onUploadImages = useCallback(async () => {
        const queuedUploadIds = appendPendingUploadMediaItems();
        if (queuedUploadIds.length) {
            setUploadMediaMeta(queuedUploadIds, formState.date, formState.description);
        }

        if (isUploadQueueStarted) {
            return;
        }

        const hasProcessableItems = getUploadMediaListSnapshot().some(
            (mediaItem) => !mediaItem.isUploading && !mediaItem.errorText
        );
        if (!hasProcessableItems) {
            return;
        }

        setUploadQueueStarted(true);
        try {
            for (
                let nextQueueItem = getUploadMediaListSnapshot().find(
                    (mediaItem) => !mediaItem.isUploading && !mediaItem.errorText
                );
                nextQueueItem;
                nextQueueItem = getUploadMediaListSnapshot().find(
                    (mediaItem) => !mediaItem.isUploading && !mediaItem.errorText
                )
            ) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await uploadMediaItem(nextQueueItem.id, (mediaItem) => ({
                        date: mediaItem.date || formState.date,
                        description: mediaItem.description || formState.description,
                        albums: mediaItem.albums || formState.albums,
                    }));
                    try {
                        // eslint-disable-next-line no-await-in-loop
                        await dispatch(getProgressMediaList()).unwrap();
                        clearUploadedProgressMediaList();
                    } catch {
                        console.error('Failed to refresh media list after upload');
                    }
                } catch {
                    console.error('File upload error');
                }
            }
        } finally {
            setUploadQueueStarted(false);
        }
    }, [
        appendPendingUploadMediaItems,
        clearUploadedProgressMediaList,
        dispatch,
        getUploadMediaListSnapshot,
        isUploadQueueStarted,
        formState.date,
        formState.description,
        formState.albums,
        setUploadQueueStarted,
        setUploadMediaMeta,
        uploadMediaItem,
    ]);

    const onSave = useCallback(() => {
        dispatch(
            editProgress({
                id: currentProgressId,
                date: formState.date,
                description: formState.description,
                albums: formState.albums,
            })
        );
    }, [isEdit, formState]);
    const onSubmit = useCallback(() => {
        dispatch(dashboardActions.changeCurrentProgressId(null));
        dispatch(dashboardActions.onModalMode(null));

        const globalLoader = (globalThis as { loader?: { hide?: () => void } }).loader;
        globalLoader?.hide?.();

        if (isEdit) {
            onSave();
        } else {
            void onUploadImages();
        }
    }, [dispatch, isEdit, onSave, onUploadImages]);

    return {
        modalMode,
        onCancel,
        isEdit,
        formState,
        onDescription,
        onDate,
        onSubmit,
        onAddNewAlbum,
        deleteSelectedAlbum,
        onChangeAlbum,
    };
};
